/**
 * The organization's GitHub installation (§5.16, §5.25), on the GitHub page of
 * its integrations: Airlock's GitHub app installed on the organization's GitHub
 * account, so its members' machines push and open pull requests with a token
 * minted for each workspace instead of each person's own sign-in.
 *
 * An administrator's alone — the Worker answers a member 404, and the panel is
 * not drawn for one. Connecting is a trip to GitHub and back: the Worker hands
 * the app's install page with a state, GitHub sends the browser back to
 * `/api/github-app/setup`, which lands here with `?github=<outcome>` to say how
 * it went.
 */
import { useCallback, useEffect, useState } from 'react';
import type React from 'react';
import type { OrgGithubConnect, OrgGithubState } from '@airlock/contracts/cloud/github-app';
import { color } from '../../theme/tokens';
import { Note } from '../../components/list';
import { Kicker } from '../../components/primitives';
import { HeaderButton } from '../../components/Shell';
import { useConfirm } from '../../components/Modal';
import { api, apiPath } from '../../lib/api';
import { proseDay } from '../../lib/format';
import { failureText } from './shared';

/** What the setup leg's redirect says, in the page's words. */
const OUTCOME: Record<string, { tone: 'teal' | 'warn'; text: string }> = {
  linked: { tone: 'teal', text: 'GitHub is connected. Members’ machines use it from their next pull request.' },
  requested: { tone: 'warn', text: 'GitHub asked an owner of that GitHub organization to approve the install. Connect again once they have.' },
  foreign: { tone: 'warn', text: 'That installation is not one your GitHub account can reach, so it was not connected.' },
  taken: { tone: 'warn', text: 'That installation is already connected to another Airlock organization.' },
  state: { tone: 'warn', text: 'That return from GitHub was not started from this page. Connect again from here.' },
  unconfigured: { tone: 'warn', text: 'This deployment has no GitHub app configured.' },
  failed: { tone: 'warn', text: 'GitHub did not complete the connection. Try again.' }
};

export function GithubAppPanel({ orgId }: { orgId: string }): React.JSX.Element | null {
  const [state, setState] = useState<OrgGithubState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, ask] = useConfirm();
  const [outcome] = useState(() => new URLSearchParams(window.location.search).get('github'));
  const [dropped, setDropped] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setState(await api.get<OrgGithubState>(apiPath`/orgs/${orgId}/github`));
    } catch (failure) {
      setError(failureText(failure));
    }
  }, [orgId]);

  useEffect(() => {
    void load();
    // The outcome is read once; the address bar is put back so a reload does not say it again.
    if (outcome) window.history.replaceState(null, '', `${window.location.pathname}${window.location.hash}`);
  }, [load, outcome]);

  const connect = async (): Promise<void> => {
    try {
      window.location.assign((await api.post<OrgGithubConnect>(apiPath`/orgs/${orgId}/github/connect`)).url);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  const disconnect = async (): Promise<void> => {
    const agreed = await ask({
      title: 'Disconnect GitHub?',
      // The modal S of §19 (item 90e): what goes, what stays.
      goes: ['The short-lived tokens members’ machines push and open pull requests with.'],
      stays: ['The app on GitHub, until you uninstall it there.', 'Each member’s own GitHub sign-in.'],
      confirm: 'Disconnect',
      danger: true
    });
    if (!agreed) return;
    try {
      setState(await api.del<OrgGithubState>(apiPath`/orgs/${orgId}/github`));
      setDropped(true);
    } catch (failure) {
      setError(failureText(failure));
    }
  };

  if (!state) return error ? <Note tone="warn">{error}</Note> : null;
  const said = outcome ? OUTCOME[outcome] : undefined;

  // The app panel of §44.6: kicker, the sentence 13.5 with the GitHub
  // organization in 600, the meta 13 with the date in prose, the verb at the
  // right as a secondary button, a hairline under the whole.
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        padding: '18px 0 16px',
        borderBottom: `1px solid ${color.line}`
      }}
    >
      {dialog}
      <Kicker>Organization app</Kicker>
      {said && <Note tone={said.tone}>{said.text}</Note>}
      {dropped && !state.link && (
        <Note tone="teal">GitHub app disconnected. Members’ machines use their own GitHub sign-in from now on.</Note>
      )}
      {!state.configured ? (
        <Note>This deployment has no GitHub app configured, so each member uses their own GitHub sign-in.</Note>
      ) : state.link ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 320px', minWidth: 0, fontSize: 13.5, color: color.ink, lineHeight: 1.5 }}>
            Installed on <strong style={{ fontWeight: 600 }}>{state.link.account}</strong>. Members’ machines push, open pull
            requests and answer reviews with a token for each workspace’s repositories, and fall back to their own sign-in
            elsewhere.
            <span style={{ display: 'block', marginTop: 2, fontSize: 13, color: color.inkSubtle }}>
              Connected by {state.link.linkedBy.label} on {proseDay(state.link.linkedAt)}
            </span>
          </span>
          <HeaderButton onClick={() => void disconnect()}>Disconnect</HeaderButton>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 320px', minWidth: 0, fontSize: 13.5, color: color.ink, lineHeight: 1.5 }}>
            Not connected. Members’ machines push and open pull requests with their own GitHub sign-in.
          </span>
          <HeaderButton onClick={() => void connect()}>Connect the app</HeaderButton>
        </div>
      )}
      {error && <Note tone="warn">{error}</Note>}
    </div>
  );
}
